import React, { useState, useRef, useEffect } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useFilterStore, DatePreset } from '../stores/filterStore';

export const ModernDateRangePicker: React.FC = () => {
  const { initialDate, finalDate, activePreset, setDates, setPreset } = useFilterStore();
  const [isOpen, setIsOpen] = useState(false);
  const [showMonths, setShowMonths] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingEnd, setSelectingEnd] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const standardPresets: { value: DatePreset; label: string }[] = [
    { value: 'currentMonth', label: 'Este Mês' },
    { value: 'lastMonth', label: 'Mês Passado' },
    { value: 'currentYear', label: 'Este Ano' },
  ];

  const monthlyPresets: { value: DatePreset; label: string }[] = [
    { value: 'jan25', label: 'Jan/25' },
    { value: 'feb25', label: 'Fev/25' },
    { value: 'mar25', label: 'Mar/25' },
    { value: 'apr25', label: 'Abr/25' },
    { value: 'may25', label: 'Mai/25' },
    { value: 'jun25', label: 'Jun/25' },
    { value: 'jul25', label: 'Jul/25' },
    { value: 'aug25', label: 'Ago/25' },
    { value: 'sep25', label: 'Set/25' },
    { value: 'oct25', label: 'Out/25' },
    { value: 'nov25', label: 'Nov/25' },
    { value: 'dec25', label: 'Dez/25' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setTempStartDate(null);
        setTempEndDate(null);
        setSelectingEnd(false);
        setShowMonths(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const handleDayClick = (day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(selectedDate);
      setTempEndDate(null);
      setSelectingEnd(true);
    } else {
      if (selectedDate < tempStartDate) {
        setTempEndDate(tempStartDate);
        setTempStartDate(selectedDate);
      } else {
        setTempEndDate(selectedDate);
      }
      setSelectingEnd(false);
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      const formatForStore = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      setDates(formatForStore(tempStartDate), formatForStore(tempEndDate));
      setIsOpen(false);
      setTempStartDate(null);
      setTempEndDate(null);
      setSelectingEnd(false);
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    setPreset(preset);
    setTempStartDate(null);
    setTempEndDate(null);
    setSelectingEnd(false);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setTempStartDate(null);
    setTempEndDate(null);
    setSelectingEnd(false);
  };

  const isDateInRange = (day: number) => {
    if (!tempStartDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

    if (tempEndDate) {
      return date >= tempStartDate && date <= tempEndDate;
    }
    return date.getTime() === tempStartDate.getTime();
  };

  const isStartDate = (day: number) => {
    if (!tempStartDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.getTime() === tempStartDate.getTime();
  };

  const isEndDate = (day: number) => {
    if (!tempEndDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.getTime() === tempEndDate.getTime();
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
    const days = [];
    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const inRange = isDateInRange(day);
      const isStart = isStartDate(day);
      const isEnd = isEndDate(day);

      days.push(
        <button
          key={day}
          onClick={() => handleDayClick(day)}
          className={`calendar-day ${inRange ? 'in-range' : ''} ${isStart ? 'start-date' : ''} ${isEnd ? 'end-date' : ''}`}
          type="button"
        >
          {day}
        </button>
      );
    }

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <button
            type="button"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </button>
          <span className="month-year">
            {currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
          </span>
          <button
            type="button"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
        <div className="weekdays">
          {weekDays.map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-grid">
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="modern-date-range-picker" ref={dropdownRef}>
      <style>{`
        .modern-date-range-picker {
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        }

        .trigger-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          
          /* ESTADO PADRÃO: Fundo e borda adaptáveis ao tema */
          background: var(--bg-widget);
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-base, 4px);
          
          color: var(--text-primary);
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          position: relative;
          z-index: 10;
        }

        /* HOVER: Aplica o gradiente vermelho e a elevação */
        .trigger-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
          
          /* Truque do gradiente na borda */
          background: linear-gradient(#fff, #fff) padding-box,
                      linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) border-box;
          border: 1px solid transparent;
          color: #0f172a;
        }

        /* Mantém o estilo "ativo" quando o dropdown está aberto, mesmo sem mouse em cima */
        .trigger-button[aria-expanded="true"] {
          background: linear-gradient(#fff, #fff) padding-box,
                      linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) border-box;
          border: 1px solid transparent;
        }

        .trigger-button:active {
          transform: translateY(0);
        }

        .date-display {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          background: white;
          border-radius: 14px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          padding: 16px;
          z-index: 1000;
          min-width: 550px;
          animation: slideDown 0.2s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .dropdown-content {
          display: flex;
          gap: 20px;
        }

        .presets-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding-right: 20px;
          border-right: 1px solid #e5e7eb;
          min-width: 160px;
        }

        .presets-title {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .preset-button {
          padding: 8px 12px;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          text-align: left;
          font-size: 13px;
          color: #374151;
          transition: all 0.2s ease;
        }

        .preset-button:hover {
          background: #f3f4f6;
          color: #ef4444;
        }

        .preset-button.active {
          background: linear-gradient(#fff, #fff) padding-box,
                      linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) border-box;
          color: #000000;
          border: 1px solid transparent;
          border-radius: 6px;
          font-weight: 500;
        }

        .toggle-months {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 500;
          color: #4b5563;
          margin-top: 4px;
        }

        .monthly-presets {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding-left: 12px;
          border-left: 2px solid #e5e7eb;
          margin-top: 4px;
          animation: slideDown 0.2s ease;
        }

        .calendar-section {
          flex: 1;
        }

        .calendar-container {
          width: 100%;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .calendar-header button {
          background: #f3f4f6;
          border: none;
          border-radius: 6px;
          padding: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          color: #374151;
        }

        .calendar-header button:hover {
          background: #e5e7eb;
          color: #ef4444;
        }

        .month-year {
          font-size: 15px;
          font-weight: 600;
          color: #1f2937;
          text-transform: capitalize;
        }

        .weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          margin-bottom: 6px;
        }

        .weekday {
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          padding: 6px;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }

        .calendar-day {
          aspect-ratio: 1;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          color: #374151;
          transition: all 0.2s ease;
          position: relative;
        }

        .calendar-day:not(.empty):hover {
          background: #f3f4f6;
          color: #ef4444;
        }

        .calendar-day.empty {
          cursor: default;
        }

        .calendar-day.in-range {
          background: rgba(239, 68, 68, 0.1);
        }

        .calendar-day.start-date,
        .calendar-day.end-date {
          background: linear-gradient(#fff, #fff) padding-box,
                      linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) border-box;
          color: #000000;
          border: 1px solid transparent;
          border-radius: 6px;
          font-weight: 600;
        }

        .calendar-day.start-date:hover,
        .calendar-day.end-date:hover {
          background: linear-gradient(#fff, #fff) padding-box,
                      linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) border-box;
          color: #000000;
        }

        .actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel {
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          transition: all 0.2s ease;
        }

        .btn-cancel:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .btn-apply {
          padding: 8px 16px;
          border: 1px solid transparent;
          background: linear-gradient(#fff, #fff) padding-box,
                      linear-gradient(135deg, #ef4444 0%, #b91c1c 100%) border-box;
          color: #000000;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .btn-apply:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
        }

        .btn-apply:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .selection-hint {
          text-align: center;
          padding: 10px;
          background: #f0f9ff;
          border-radius: 6px;
          margin-bottom: 12px;
          font-size: 12px;
          color: #0369a1;
        }
      `}</style>

      <button
        className="trigger-button"
        onClick={() => {
          console.log('Trigger button clicked, current state:', isOpen);
          setIsOpen(!isOpen);
        }}
        type="button"
      >
        <CalendarMonthIcon sx={{ fontSize: 20 }} />
        <div className="date-display">
          <span>{formatDateDisplay(initialDate)}</span>
          <span>→</span>
          <span>{formatDateDisplay(finalDate)}</span>
        </div>
      </button>

      {isOpen && (
        <div className="dropdown">
          <div className="dropdown-content">
            <div className="presets-section">
              <div className="presets-title">Períodos Rápidos</div>
              {standardPresets.map(preset => (
                <button
                  key={preset.value}
                  className={`preset-button ${activePreset === preset.value ? 'active' : ''}`}
                  onClick={() => handlePresetClick(preset.value)}
                  type="button"
                >
                  {preset.label}
                </button>
              ))}

              <button
                className="preset-button toggle-months"
                onClick={() => setShowMonths(!showMonths)}
                type="button"
              >
                Meses {showMonths ? '▲' : '▼'}
              </button>

              {showMonths && (
                <div className="monthly-presets">
                  {monthlyPresets.map(preset => (
                    <button
                      key={preset.value}
                      className={`preset-button ${activePreset === preset.value ? 'active' : ''}`}
                      onClick={() => handlePresetClick(preset.value)}
                      type="button"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="calendar-section">
              {selectingEnd && tempStartDate && (
                <div className="selection-hint">
                  Selecione a data final do período
                </div>
              )}
              {renderCalendar()}
              <div className="actions">
                <button className="btn-cancel" onClick={handleCancel} type="button">
                  Cancelar
                </button>
                <button
                  className="btn-apply"
                  onClick={handleApply}
                  disabled={!tempStartDate || !tempEndDate}
                  type="button"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};